import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Calendar, Images, Play, Film } from 'lucide-react';
import { useFieldAlbums, FIELD_CATEGORIES, FieldAlbum } from '../../hooks/useFieldAlbums';
import { thumb, IMG_PLACEHOLDER } from '../../lib/imageUtils';
import { youtubeThumbnail } from '../../lib/youtubeUtils';
import VideoLightbox from '../media/VideoLightbox';

const formatDate = (d?: string) => d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
const catLabel = (v: string) => FIELD_CATEGORIES.find((c) => c.value === v)?.label || v;

const typeBadge = (ct?: string): { label: string; icon: React.ReactNode } | null => {
  switch (ct) {
    case 'video': return { label: 'Vidéo', icon: <Play size={11} /> };
    case 'photo_video': return { label: 'Photo + Vidéo', icon: <Film size={11} /> };
    case 'photo': return { label: 'Photo', icon: <Images size={11} /> };
    case 'album': return { label: 'Album', icon: <Images size={11} /> };
    default: return null;
  }
};

const cardThumb = (a: FieldAlbum): string => {
  if (a.content_type === 'video' || a.content_type === 'photo_video') {
    if (a.video_thumbnail_custom) return a.video_thumbnail_custom;
    if (a.video_id) return youtubeThumbnail(a.video_id, 'hq');
  }
  return a.cover_image ? thumb(a.cover_image, 700) : '';
};

const FieldHighlights: React.FC = () => {
  const { albums, loading } = useFieldAlbums(true);
  const [videoModal, setVideoModal] = useState<{ id: string; title: string } | null>(null);

  if (loading || albums.length === 0) return null;

  const hasVideo = (a: FieldAlbum) => (a.content_type === 'video' || a.content_type === 'photo_video') && !!a.video_id;

  return (
    <section className="relative overflow-hidden bg-white py-14 sm:py-20 lg:py-24">
      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex flex-col items-start gap-4 sm:mb-10 sm:flex-row sm:items-end sm:justify-between sm:gap-8">
          <div>
            <span className="mb-3 inline-block rounded-full bg-primary-50 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-primary-700">Proxy sur le terrain</span>
            <h2 className="text-3xl font-black leading-[1.05] tracking-[-0.025em] text-neutral-900 sm:text-4xl lg:text-5xl">
              Nos <span className="text-primary-600">moments forts</span>
            </h2>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-neutral-600 sm:text-base">
              Événements, partenariats, formations et démonstrations : Proxy Emobility en action en Afrique de l'Ouest.
            </p>
          </div>
          <Link to="/sur-le-terrain" className="group inline-flex items-center gap-2 rounded-xl bg-neutral-900 px-5 py-3 text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-primary-600 whitespace-nowrap">
            Voir tous nos moments forts
            <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {albums.map((album) => {
            const badge = typeBadge(album.content_type);
            const isVideo = hasVideo(album);
            const img = cardThumb(album);

            const Inner = (
              <>
                <div className="relative aspect-[4/3] overflow-hidden bg-neutral-100">
                  {img ? (
                    <img src={img} alt={album.title} loading="lazy" decoding="async"
                      onError={(e) => { (e.target as HTMLImageElement).src = IMG_PLACEHOLDER; }}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-neutral-300"><Images size={40} /></div>
                  )}
                  {isVideo && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/90 shadow-lg transition-transform duration-300 group-hover:scale-110">
                        <Play size={24} className="ml-1 text-neutral-900" fill="currentColor" />
                      </span>
                    </div>
                  )}
                  {badge && (
                    <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-neutral-950/85 px-2.5 py-1 text-[10px] font-bold text-white backdrop-blur">
                      {badge.icon} {badge.label}
                    </span>
                  )}
                  <span className="absolute left-3 top-3 rounded-full bg-white/90 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-neutral-800 backdrop-blur">
                    {catLabel(album.category)}
                  </span>
                  {!isVideo && (album.photo_count ?? 0) > 0 && (
                    <span className="absolute bottom-3 right-3 inline-flex items-center gap-1 rounded-full bg-neutral-950/85 px-2.5 py-1 text-[10px] font-bold text-white backdrop-blur">
                      <Images size={11} /> {album.photo_count}
                    </span>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-black text-neutral-900 leading-tight">{album.title}</h3>
                  {album.short_description && <p className="mt-1.5 text-sm text-neutral-600 line-clamp-2">{album.short_description}</p>}
                  <div className="mt-3 flex items-center gap-3 text-[11px] text-neutral-400">
                    {album.event_date && <span className="inline-flex items-center gap-1"><Calendar size={12} /> {formatDate(album.event_date)}</span>}
                    {album.location && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {album.location}</span>}
                  </div>
                </div>
              </>
            );

            const cardClass = "group block overflow-hidden rounded-2xl border border-neutral-200 bg-white text-left transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_-20px_rgba(0,0,0,0.2)]";

            if (album.content_type === 'video' && isVideo) {
              return (
                <button key={album.id} onClick={() => setVideoModal({ id: album.video_id!, title: album.title })} className={cardClass + ' w-full'}>
                  {Inner}
                </button>
              );
            }
            return (
              <Link key={album.id} to={`/sur-le-terrain?album=${album.slug || album.id}`} className={cardClass}>
                {Inner}
              </Link>
            );
          })}
        </div>
      </div>

      {videoModal && <VideoLightbox videoId={videoModal.id} title={videoModal.title} onClose={() => setVideoModal(null)} />}
    </section>
  );
};

export default FieldHighlights;
